'use client';

import { useEffect, useRef, useState } from 'react';

interface KlaviyoFormProps {
  id: string;
}

export function KlaviyoForm({ id }: KlaviyoFormProps) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(300); // Start with a reasonable default height
  const [isLoading, setIsLoading] = useState(true);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);

  // Set up the iframe URL on the client side
  useEffect(() => {
    // Add a unique cache-busting parameter to force iframe reload
    const cacheBuster = `&cache=${Date.now()}`;
    setIframeUrl(`/klaviyo-form?id=${id}${cacheBuster}`);
  }, [id]);

  useEffect(() => {
    if (!iframeUrl) return;

    // Function to handle messages from the iframe
    const handleMessage = (event: MessageEvent) => {
      // Make sure the message is from our iframe
      if (
        event.data &&
        typeof event.data === 'object' &&
        event.data.type === 'klaviyo-form-height'
      ) {
        const newHeight = event.data.height;
        if (newHeight && newHeight > 100) {
          setHeight(newHeight);
          setIsLoading(false);
        }
      }
    };

    // Add message listener
    window.addEventListener('message', handleMessage);

    // Set up a fallback in case the message approach doesn't work
    const checkHeight = () => {
      try {
        if (ref.current?.contentWindow?.document.body) {
          const newHeight =
            ref.current.contentWindow.document.body.scrollHeight;
          if (newHeight > 100) {
            setHeight(newHeight);
            setIsLoading(false);
          }
        }
      } catch (e) {
        // Likely a cross-origin error, we'll rely on the message approach
        console.log('Could not access iframe content directly:', e);
      }
    };

    // Try multiple times with increasing delays
    const attempts = [500, 1000, 2000, 3000, 5000];
    const timeouts: number[] = [];

    attempts.forEach((delay) => {
      const timeout = window.setTimeout(checkHeight, delay);
      timeouts.push(timeout);
    });

    // Set a timeout to stop showing loading state even if we can't determine height
    const loadingTimeout = window.setTimeout(() => {
      setIsLoading(false);
    }, 6000);

    return () => {
      window.removeEventListener('message', handleMessage);
      timeouts.forEach((timeout) => window.clearTimeout(timeout));
      window.clearTimeout(loadingTimeout);
    };
  }, [iframeUrl]);

  // Don't render the iframe until we have the URL (client-side only)
  if (!iframeUrl) {
    return (
      <div className="bg-gray-200 h-64 w-full animate-pulse rounded"></div>
    );
  }

  return (
    <>
      <iframe
        src={iframeUrl}
        ref={ref}
        height={height + 'px'}
        className="w-full border-none"
        scrolling="no"
        title="Klaviyo Form"
        onLoad={() => {
          // Try to get height after iframe loads
          setTimeout(() => {
            try {
              if (ref.current?.contentWindow?.document.body) {
                const newHeight =
                  ref.current.contentWindow.document.body.scrollHeight;
                if (newHeight > 100) {
                  setHeight(newHeight);
                  setIsLoading(false);
                }
              }
            } catch (e) {
              // Likely a cross-origin error
              console.log('Could not access iframe content on load:', e);
            }
          }, 1000);
        }}
      />
      {isLoading && (
        <div className="bg-gray-200 absolute left-0 top-0 z-10 h-64 w-full animate-pulse rounded"></div>
      )}
    </>
  );
}
